import type { GridApiCommon } from '@mui/x-data-grid';
import type { InvoiceOverview } from '@vy/api-definition';
import type { InvoiceBillingStatus, InvoicePdfType } from '@vy/types';
import { INVOICE_REVIEW_STATUSES } from '@vy/types';
import {
  awaitAllSafely,
  extract,
  extractIds,
  isEmptyArray,
  isNotNullOrUndefined,
  sanitizeFilename,
  validateNotEmptyArray,
  validateNotNullOrUndefined,
  vyDate,
} from '@vy/utils';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import { useCallback } from 'react';
import { useMutation } from 'react-query';
import { useExportUsageToMarketplace, useMorningExportInvoices, usePaddleExportInvoices, useQuickBooksExportInvoices, useSyncNetSuiteInvoice, useSyncNetSuiteInvoiceSalesOrder, useXeroExportInvoices } from '../../../hooks/requests/integrations';
import { useSyncToStripe } from '../../../hooks/requests/use-sync-stripe';
import { useUpdateInvoicesBillingStatuses } from '../../../hooks/requests/use-update-invoices-billing-statuses';
import { useUpdateInvoicesReviewStatuses } from '../../../hooks/requests/use-update-invoices-review-statuses';
import { useGetInvoiceUsageEventsReport } from '../../../hooks/requests/use-get-invoice-usage-events-report';
import { useIsIssueOnEndOfMonthActive } from '../../../hooks/use-is-issue-on-end-of-month-active';
import { useGetInvoicePdf } from '../../../hooks/use-get-invoice-pdf';
import { buildUsageEventsReportFilename } from '../utils/save-usage-events-report';
import { runMutation } from '../../../utils/vy-mutation';
import { getBillingStatusLabel } from '../InvoicesPage/invoices-page-components/InvoiceBillingStatusLabel';
import { generateCSV } from '../InvoicesPage/invoices-page-utils/invoices-page-generate-csv';
import { useNonPagedInvoicesOverviewsFromParams } from '../../../hooks/requests/use-invoices-overviews/use-non-paged-invoices-overviews-from-params';

const CSV_EXCLUDED_COLUMNS = ['__check__', 'actionMenu'];

type UseInvoicesActionsProps = {
  selectedInvoices: InvoiceOverview[];
  apiRef?: React.MutableRefObject<GridApiCommon | null>;
  onSuccess?: () => Promise<void>;
};

export const useInvoicesActions = ({
  selectedInvoices,
  apiRef,
  onSuccess,
}: UseInvoicesActionsProps) => {
  const issueOnEndOfMonth = useIsIssueOnEndOfMonthActive();

  const updateInvoicesBillingStatusesMutation = useUpdateInvoicesBillingStatuses();
  const updateInvoicesReviewStatusesMutation = useUpdateInvoicesReviewStatuses();
  const downloadInvoicePdfMutation = useGetInvoicePdf();
  const syncInvoicesToStripeMutation = useSyncToStripe();
  const syncToNetSuiteMutation = useSyncNetSuiteInvoice();
  const syncToNetSuiteSalesOrderMutation = useSyncNetSuiteInvoiceSalesOrder();
  const allInvoicesMutation = useNonPagedInvoicesOverviewsFromParams();
  const syncToQuickBooksMutation = useQuickBooksExportInvoices();
  const syncToMorningMutation = useMorningExportInvoices();
  const syncToMarketplaceMutation = useExportUsageToMarketplace();
  const downloadUsageEventsReportMutation = useGetInvoiceUsageEventsReport();
  const syncToXeroMutation = useXeroExportInvoices();
  const syncToPaddleMutation = usePaddleExportInvoices();

  const downloadUsageEventsReportsMutation = useMutation({
    mutationFn: async () => {
      const invoiceIds = extractIds(selectedInvoices);

      const downloadPromises = invoiceIds.map(async (invoiceId) => {
        const { blob, isZip } = await downloadUsageEventsReportMutation.mutateAsync({ invoiceId });
        const invoice = selectedInvoices.find(({ id }) => id === invoiceId);
        validateNotNullOrUndefined(invoice, 'invoice');
        return { invoice, blob, isZip };
      });

      const results = await awaitAllSafely(...downloadPromises);

      const zip = new JSZip();
      for (const { invoice, blob, isZip } of results) {
        const filename = buildUsageEventsReportFilename(invoice.customer.name, invoice.name, isZip);
        zip.file(filename, blob);
      }


      const content = await zip.generateAsync({ type: 'blob' });
      saveAs(content, 'usage-events-reports.zip');
    },
  });

  const handleDownloadUsageEventsReports = async () => {
    await runMutation(
      downloadUsageEventsReportsMutation,
      undefined,
      {
        pending: 'Downloading usage events reports...',
        success: 'Usage events reports downloaded',
        error: 'Failed to download usage events reports',
      },
    );
  };

  const downloadingInvoicesPdfsMutation = useMutation({
    mutationFn: async ({ type }: { type: InvoicePdfType }) => {
      const invoicesIds = extractIds(selectedInvoices);

      const downloadingInvoicesPdfsPromises = invoicesIds.map((invoiceId) =>
        downloadInvoicePdfMutation.mutateAsync({
          invoiceId,
          type,
        }),
      );

      const pdfs = await awaitAllSafely(...downloadingInvoicesPdfsPromises);

      const zip = new JSZip();

      for (const { invoiceId, encodedPdf } of pdfs) {
        const invoice = selectedInvoices.find(({ id }) => id === invoiceId);
        validateNotNullOrUndefined(invoice, 'invoice');

        const response = await fetch(`data:application/pdf;base64,${encodedPdf}`);
        const pdfBlob = await response.blob();

        const rawFileName = `${invoice.customer.name} - ${invoice.name}`;
        const sanitizedFileName = sanitizeFilename(rawFileName);
        const fileNameWithExtension = `${sanitizedFileName}.pdf`;

        zip.file(fileNameWithExtension, pdfBlob);
      }

      const content = await zip.generateAsync({ type: 'blob' });
      saveAs(content, 'invoices.zip');
    },
  });

  const handleDownloadInvoicesPdfs = async (pdfType: InvoicePdfType) => {
    await runMutation(
      downloadingInvoicesPdfsMutation,
      { type: pdfType },
      {
        pending: 'Downloaded invoices...',
        success: 'Invoices downloaded',
        error: 'Failed to download invoices',
      },
    );
  };

  const handleApproveSelectedInvoices = useCallback(async () => {
    validateNotEmptyArray(selectedInvoices, 'selectedInvoices');
    const invoiceCustomers = extract(selectedInvoices, 'customer');
    const customerIds = extract(invoiceCustomers, 'id');

    await runMutation(
      updateInvoicesReviewStatusesMutation,
      {
        customerIds,
        invoicesByNewStatus: {
          [INVOICE_REVIEW_STATUSES.Approved]: extract(selectedInvoices, 'id'),
        },
      },
      {
        pending: 'Approving invoices...',
        success: 'Invoices approved',
        error: 'Failed to approve invoices',
      },
      {
        onSuccess,
      },
    );
  }, [selectedInvoices, updateInvoicesReviewStatusesMutation, onSuccess]);

  const handleExportSelectedInvoices = async () => {
    const shouldExportAll = isEmptyArray(selectedInvoices);
    const invoicesToExport = await getInvoicesToExport(shouldExportAll);

    let visibleFields: string[] | undefined;
    if (isNotNullOrUndefined(apiRef?.current)) {
      visibleFields = apiRef.current
        .getVisibleColumns()
        .map((column) => column.field)
        .filter((field) => !CSV_EXCLUDED_COLUMNS.includes(field));
    }

    const csvContent = generateCSV(invoicesToExport ?? [], visibleFields, { issueOnEndOfMonth });
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });

    const fileName = vyDate().format('MMMM, DD, YYYY');
    saveAs(blob, `${fileName}.csv`);
  };

  const getInvoicesToExport = async (exportAll: boolean) => {
    if (!exportAll) {
      return selectedInvoices;
    }

    const allInvoicesResponse = await runMutation(allInvoicesMutation, undefined, {
      pending: 'Downloading all invoices...',
      success: 'All invoices downloaded',
      error: 'Failed to download all invoices',
    });

    return allInvoicesResponse?.invoicesOverviews ?? [];
  };

  const handleSyncToStripe = useCallback(async () => {
    validateNotEmptyArray(selectedInvoices, 'selectedInvoices');

    await runMutation(
      syncInvoicesToStripeMutation,
      { invoiceIds: extract(selectedInvoices, 'id') },
      {
        pending: 'Syncing to Stripe...',
        success: 'Synced to Stripe',
        error: 'Failed to sync to Stripe',
      },
    );
  }, [syncInvoicesToStripeMutation, selectedInvoices]);

  const handleSyncToNetSuite = async (filteredInvoices: InvoiceOverview[]) => {
    const netSuiteSyncInvoicesIds = filteredInvoices.map((invoice) => invoice.id);

    await runMutation(
      syncToNetSuiteMutation,
      { invoicesIds: netSuiteSyncInvoicesIds },
      {
        pending: 'Requesting sync to NetSuite...',
        success: 'Sync invoice initialized',
        error: 'Failed to initialize sync to NetSuite',
      },
    );
  };

  const handleSyncToNetSuiteSalesOrder = async (filteredInvoices: InvoiceOverview[]) => {
    const invoicesIds = filteredInvoices.map((invoice) => invoice.id);

    await runMutation(
      syncToNetSuiteSalesOrderMutation,
      { invoicesIds },
      {
        pending: "Invoice sync started. You'll be notified when it's complete.",
        error: 'Failed to initialize sync to NetSuite as Sales Order',
      },
    );
  };

  const handleSyncToQuickBooks = async () => {
    await runMutation(
      syncToQuickBooksMutation,
      { invoiceIds: extract(selectedInvoices, 'id') },
      {
        pending: 'Syncing to QuickBooks...',
        success: 'Synced to QuickBooks',
        error: 'Failed to sync to QuickBooks',
      },
    );
  };

  const handleSyncToMorning = async () => {
    await runMutation(
      syncToMorningMutation,
      { invoiceIds: extract(selectedInvoices, 'id') },
      {
        pending: 'Syncing to Morning...',
        success: 'Synced to Morning',
        error: 'Failed to sync to Morning',
      },
    );
  };

  const handleSyncToMarketplace = async () => {
    await runMutation(
      syncToMarketplaceMutation,
      { invoicesIds: extract(selectedInvoices, 'id') },
      {
        pending: 'Exporting usage to Marketplace...',
        success: 'Usage export initiated',
        error: 'Failed to export usage to Marketplace',
      },
    );
  };

  const handleSyncToXero = async () => {
    await runMutation(
      syncToXeroMutation,
      { invoiceIds: extract(selectedInvoices, 'id') },
      {
        pending: 'Requesting sync to Xero...',
        success: 'Sync invoices initialized',
        error: 'Failed to initialize sync to Xero',
      },
    );
  };

  const handleSyncToPaddle = async () => {
    await runMutation(
      syncToPaddleMutation,
      { invoiceIds: extract(selectedInvoices, 'id') },
      {
        pending: 'Requesting sync to Paddle...',
        success: 'Sync invoices initialized',
        error: 'Failed to initialize sync to Paddle',
      },
    );
  };

  const handleUpdateInvoicesBillingStatus = useCallback(
    async (billingStatus: InvoiceBillingStatus) => {
      validateNotEmptyArray(selectedInvoices, 'selectedInvoices');

      const displayBillingStatus = getBillingStatusLabel(billingStatus);

      await runMutation(
        updateInvoicesBillingStatusesMutation,
        {
          invoiceStatus: billingStatus,
          invoiceIds: extract(selectedInvoices, 'id'),
        },
        {
          pending: `Updating invoice status to ${displayBillingStatus}...`,
          success: `Invoice status updated to ${displayBillingStatus}`,
          error: `Failed to update invoice status to ${displayBillingStatus}`,
        },
        {
          onSuccess,
        },
      );
    },
    [selectedInvoices, updateInvoicesBillingStatusesMutation, onSuccess],
  );

  return {
    handleDownloadInvoicesPdfs,
    handleDownloadUsageEventsReports,
    handleApproveSelectedInvoices,
    handleExportSelectedInvoices,
    handleSyncToStripe,
    handleSyncToNetSuite,
    handleSyncToNetSuiteSalesOrder,
    handleSyncToQuickBooks,
    handleSyncToMorning,
    handleSyncToMarketplace,
    handleSyncToXero,
    handleSyncToPaddle,
    handleUpdateInvoicesBillingStatus,
    downloadingInvoicesPdfsMutation,
  };
};
